import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, MapPin, Clock, Phone, Mail, DollarSign, ArrowLeft, Bookmark, BookmarkCheck, Trophy, PhoneCall, Zap, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import Header from '../components/Header';
import VerifiedBadge from '../components/VerifiedBadge';
import { api, formatError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { trackCall } from '../components/CallFeedbackPrompt';
import { toast } from 'sonner';

const BM_KEY = 'tt_bookmarks';
const readBookmarks = () => { try { return JSON.parse(localStorage.getItem(BM_KEY) || '[]'); } catch { return []; } };
const writeBookmarks = (list) => localStorage.setItem(BM_KEY, JSON.stringify(list));

// Compliance trust chip (public, no doc contents shown)
function ComplianceChip({ label, status }) {
  const map = {
    current:  { icon: <ShieldCheck size={12} />,   className: 'bg-[#2A9D8F]/10 text-[#2A9D8F]',    text: label },
    expiring: { icon: <ShieldAlert size={12} />,   className: 'bg-[#E9C46A]/20 text-[#B08911]',    text: `${label} expiring` },
    expired:  { icon: <ShieldAlert size={12} />,   className: 'bg-[#E76F51]/10 text-[#E76F51]',    text: `${label} expired` },
    'no-expiry': { icon: <ShieldCheck size={12} />, className: 'bg-[#F3F2EE] text-[#5C5B5A]',      text: label },
    missing:  { icon: <ShieldQuestion size={12} />, className: 'bg-[#F3F2EE] text-[#8A8988]',       text: `No ${label.toLowerCase()}` },
  };
  const s = map[status] || map.missing;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] ${s.className}`}>
      {s.icon} {s.text}
    </span>
  );
}

export default function TradieProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [trade, setTrade] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  const load = async () => {
    try {
      const [t, r] = await Promise.all([api.getTrade(id), api.getReviews(id)]);
      setTrade(t.data); setReviews(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { setBookmarked(readBookmarks().some((b) => b.id === id)); }, [id]);

  const toggleBookmark = () => {
    const list = readBookmarks();
    if (list.some((b) => b.id === id)) {
      writeBookmarks(list.filter((b) => b.id !== id));
      setBookmarked(false); toast.success('Removed from saved');
    } else if (trade) {
      const entry = { id: trade.id, business_name: trade.business_name, categories: trade.categories, suburb: trade.suburb, avatar_url: trade.avatar_url, phone: trade.phone };
      writeBookmarks([entry, ...list]);
      setBookmarked(true); toast.success('Saved');
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await api.postReview(id, { rating, comment: comment.trim() });
      setComment(''); setRating(5);
      toast.success('Cheers — review posted.');
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const onCall = () => trade && trackCall(trade.id, trade.business_name);

  if (!trade) return <div className="min-h-screen bg-[#FAF9F6]"><Header /><div className="mx-auto max-w-4xl px-6 py-16">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="tradie-profile-page">
      <Header />
      <div className="mx-auto max-w-5xl px-6 py-8 md:px-10">
        <div className="flex items-center justify-between">
          <Link to="/search" className="inline-flex items-center gap-2 text-sm text-[#5C5B5A] hover:text-[#1A362D]" data-testid="back-to-search">
            <ArrowLeft size={14} /> All trades
          </Link>
          <button onClick={toggleBookmark} data-testid="bookmark-btn" className="inline-flex items-center gap-2 border border-[#E5E4E0] bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#1A362D] hover:border-[#1A362D]">
            {bookmarked ? <><BookmarkCheck size={14} /> Saved</> : <><Bookmark size={14} /> Save</>}
          </button>
        </div>

        {trade.claimed === false && (
          <div className="mt-6 border-l-4 border-[#B08911] bg-[#FFF8E7] p-6" data-testid="unclaimed-banner">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#B08911] text-white font-heading text-lg font-bold">?</div>
              <div className="flex-1">
                <div className="font-heading text-lg font-bold text-[#1C1B1A]">This listing hasn't been claimed yet.</div>
                <p className="mt-1 text-sm text-[#5C5B5A]">
                  Real Tradies created this listing for <b>{trade.business_name}</b> from publicly available information. Are you the owner? Claim it free
                  to add your photos, portfolio, hourly rate — and get the green Verified tick once we've checked your licence and insurance.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link to={`/register?claim=${trade.id}`} className="inline-flex items-center gap-2 bg-[#1A362D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-white hover:bg-[#244A3E]" data-testid="claim-listing-btn">
                    Claim this listing free
                  </Link>
                  <Link to={`/removal-request?tradie=${trade.id}`} className="text-xs font-semibold uppercase tracking-[0.15em] text-[#5C5B5A] underline underline-offset-4 hover:text-[#1C1B1A]" data-testid="removal-request-link">
                    Not your business? Request removal
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 border border-[#E5E4E0] bg-white">
          <div className="flex flex-col gap-6 p-6 md:flex-row md:p-10">
            {trade.avatar_url ? (
              <img src={trade.avatar_url} alt={trade.business_name} className="h-40 w-40 shrink-0 object-cover" />
            ) : (
              <div className="flex h-40 w-40 shrink-0 items-center justify-center bg-[#F3F2EE] font-heading text-6xl font-bold text-[#1A362D]">
                {trade.business_name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl" data-testid="tradie-name">{trade.business_name}</h1>
                {trade.verified && <VerifiedBadge variant="large" />}
                {trade.top_rated && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#1A362D] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-white">
                    <Trophy size={12} /> Top rated
                  </span>
                )}
                {trade.emergency_24_7 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#E76F51] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-white" data-testid="emergency-badge-large">
                    <Zap size={12} /> 24/7 Emergency
                  </span>
                )}
              </div>

              <div className="mt-2 flex flex-wrap gap-2 text-xs uppercase tracking-[0.15em] text-[#8A8988]">
                {(trade.categories || []).map((c) => <span key={c}>{c.replace('_', ' ')}</span>)}
              </div>

              {/* Compliance trust chips */}
              <div className="mt-4 flex flex-wrap gap-2" data-testid="compliance-chips">
                <ComplianceChip label="Licensed" status={trade.license_status} />
                <ComplianceChip label="Insured" status={trade.insurance_status} />
                <ComplianceChip label="Workers Comp" status={trade.workers_comp_status} />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#5C5B5A]">
                <span className="inline-flex items-center gap-1.5">
                  <Star size={16} className="fill-[#E9C46A] text-[#E9C46A]" />
                  <span className="font-semibold text-[#1C1B1A]">{trade.avg_rating || '—'}</span>
                  <span>({trade.review_count} reviews)</span>
                </span>
                {trade.answer_rate != null && (
                  <span className="inline-flex items-center gap-1.5 text-[#2A9D8F]" data-testid="answer-rate-large">
                    <PhoneCall size={16} /> <b>{trade.answer_rate}%</b> answer their calls
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5"><MapPin size={16} /> {trade.suburb}</span>
                <span className="inline-flex items-center gap-1.5"><Clock size={16} /> {trade.response_time}</span>
                {trade.hourly_rate && <span className="inline-flex items-center gap-1.5 font-semibold text-[#1A362D]"><DollarSign size={16} />{trade.hourly_rate}/hr</span>}
              </div>

              {trade.bio && <p className="mt-6 text-base leading-relaxed text-[#1C1B1A]">{trade.bio}</p>}

              <div className="mt-6 flex flex-wrap gap-3">
                {trade.phone && (
                  <a href={`tel:${trade.phone}`} onClick={onCall} data-testid="call-btn" className="btn-accent">
                    <Phone size={16} /> {trade.phone}
                  </a>
                )}
                {trade.email && (
                  <a href={`mailto:${trade.email}`} data-testid="email-btn" className="btn-secondary">
                    <Mail size={16} /> Email
                  </a>
                )}
              </div>
            </div>
          </div>

          {trade.service_areas?.length > 0 && (
            <div className="border-t border-[#E5E4E0] px-6 py-4 text-sm text-[#5C5B5A] md:px-10">
              <span className="font-semibold text-[#1C1B1A]">Service areas:</span> {trade.service_areas.join(' · ')}
            </div>
          )}
        </div>

        {trade.portfolio?.length > 0 && (
          <div className="mt-10">
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8A8988]">Recent work</div>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
              {trade.portfolio.map((url, i) => <img key={i} src={url} alt="" className="aspect-square w-full object-cover" />)}
            </div>
          </div>
        )}

        <div className="mt-12">
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8A8988]">Reviews</div>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#1C1B1A]">What locals actually say</h2>

          <div className="mt-6 space-y-4" data-testid="reviews-list">
            {reviews.length === 0 && <div className="border border-[#E5E4E0] bg-white p-6 text-[#8A8988]">No reviews yet — be the first.</div>}
            {reviews.map((r) => (
              <div key={r.id} className="border border-[#E5E4E0] bg-white p-6" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-[#1C1B1A]">{r.author_name}</div>
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map((n) => <Star key={n} size={14} className={n <= r.rating ? 'fill-[#E9C46A] text-[#E9C46A]' : 'text-[#E5E4E0]'} />)}
                  </div>
                </div>
                <p className="mt-2 text-sm text-[#5C5B5A]">{r.comment}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 border border-[#E5E4E0] bg-white p-6" data-testid="review-form">
            <div className="font-heading text-xl font-bold text-[#1C1B1A]">Leave a review</div>
            {!user ? (
              <div data-testid="review-signin-gate">
                <p className="mt-1 text-sm text-[#5C5B5A]">
                  Reviews come from real, signed-in locals only — that's what keeps these ratings worth trusting.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link to="/login" className="btn-primary" data-testid="review-signin-btn">Sign in to review</Link>
                  <Link to="/register" className="border border-[#1C1B1A] px-5 py-3 text-sm font-semibold text-[#1C1B1A] transition-colors hover:bg-[#1C1B1A] hover:text-white" data-testid="review-register-btn">
                    Create a free account
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <p className="mt-1 text-sm text-[#5C5B5A]">Posting as <span className="font-semibold text-[#1C1B1A]">{user.name}</span> — one review per tradie.</p>
                <form onSubmit={submitReview} className="mt-4 space-y-4">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((n) => (
                      <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`} className="p-1">
                        <Star size={26} className={n <= rating ? 'fill-[#E9C46A] text-[#E9C46A]' : 'text-[#E5E4E0]'} />
                      </button>
                    ))}
                  </div>
                  <textarea required value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
                    placeholder="How was the work? Would you hire them again?"
                    data-testid="review-comment-input" className="w-full border border-[#E5E4E0] bg-white p-3 text-sm outline-none focus:border-[#1A362D]" />
                  <button type="submit" disabled={submitting || !comment.trim()} data-testid="submit-review-btn" className="btn-primary disabled:opacity-40">
                    {submitting ? 'Posting...' : 'Post review'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
